import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import RoleSelector from "@/components/RoleSelector";
import Logo from "@/components/Logo";
import { registerServiceWorker, subscribePush } from "@/lib/push";
import api from "@/lib/api";
import {
  FlaskConical, LayoutDashboard, FolderKanban, CalendarDays,
  BookOpen, Settings, Search, Moon, Sun, LogOut, Menu, X, Users, ShieldCheck,
} from "lucide-react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/experiments", label: "Experiments", icon: FlaskConical },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/notes", label: "Notes", icon: BookOpen },
  { to: "/labs", label: "Labs", icon: Users },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/check").then((r) => setIsAdmin(Boolean(r.data?.is_admin)))
      .catch(() => setIsAdmin(false));
  }, [user]);

  const navItems = isAdmin
    ? [...nav, { to: "/admin", label: "Admin", icon: ShieldCheck }]
    : nav;

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/experiments?q=${encodeURIComponent(search.trim())}`);
    }
  };

  // Register SW, request push permission (once), send timezone offset
  useEffect(() => {
    registerServiceWorker();
    (async () => {
      try {
        const offset = -new Date().getTimezoneOffset(); // minutes east of UTC
        await api.post("/users/me/preferences", { utc_offset_minutes: offset, digest_hour: 9 });
      } catch {}
    })();
    // Ask push permission on first interaction; silently skip if denied
    const tryPush = async () => {
      try {
        if ("Notification" in window && Notification.permission === "default") {
          // Only prompt from a user gesture later via Settings; do not auto-prompt here.
        } else if (Notification.permission === "granted") {
          await subscribePush(); // refresh/maintain subscription
        }
      } catch {}
    };
    tryPush();
  }, []);

  return (
    <div className="flex h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Sidebar */}
      <aside
        className={`${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:sticky top-0 z-40 lg:z-0 w-64 h-screen border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col transition-transform`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-zinc-200 dark:border-zinc-800">
          <Logo size={30} />
          <span className="font-sans font-bold tracking-tight text-lg">
            Resy<span className="text-blue-600">Note</span>
          </span>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-body transition-colors ${
                  isActive
                    ? "bg-zinc-100 dark:bg-zinc-900 text-zinc-900 dark:text-zinc-50 font-medium"
                    : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-900 hover:text-zinc-900 dark:hover:text-zinc-50"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            logged in as
          </div>
          <div className="truncate text-sm font-body mt-0.5">{user?.email}</div>
          <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-mono text-zinc-500">
            <Link to="/contact" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="sidebar-link-contact">Contact</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="sidebar-link-terms">Terms</Link>
            <span>·</span>
            <Link to="/refund-policy" className="hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="sidebar-link-refund">Refund</Link>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-zinc-950/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex items-center gap-3 px-4 md:px-6 sticky top-0 z-20">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <form onSubmit={onSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search experiments, tags, objectives…"
                className="pl-9 font-body bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"
                data-testid="global-search-input"
              />
            </div>
          </form>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle"
            title="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-9 w-9 rounded-full bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 grid place-items-center text-sm font-medium hover:ring-2 hover:ring-blue-500/30"
                data-testid="user-menu"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="h-full w-full rounded-full object-cover" />
                ) : (
                  (user?.name || user?.email || "U").slice(0, 1).toUpperCase()
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-body">
                <div className="font-semibold truncate">{user?.name || "Researcher"}</div>
                <div className="text-xs text-zinc-500 truncate font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
        <RoleSelector />
      </div>
    </div>
  );
}
